// Formatting shared by every commentHub surface — the conversation (widget and
// panel), its teasers (rail card, mobile bar, end block) and the notification
// tray. Extracted from CommentHubConversations when the tray needed the same
// helpers; a second copy would have drifted the day either one changed its
// wording.

const minute = 60000
const hour = 60 * minute
const day = 24 * hour

// Avatar-fallback palette in the spirit of the previous provider's
// per-user colors; picked by author id so a user keeps their color.
const avatarColors = ['#0074e0', '#f05f22', '#00a45a', '#7d4cdb', '#00879e', '#d6356e']

export function avatarColor(userId: number): string {
  return avatarColors[userId % avatarColors.length] ?? avatarColors[0]
}

// Rendered uppercase via CSS to match the "7 HRS AGO" style
export function timeAgo(iso: string): string {
  const date = new Date(iso)
  const diff = Date.now() - date.getTime()
  if (diff < minute) {
    return 'just now'
  }
  if (diff < hour) {
    const m = Math.floor(diff / minute)
    return `${m} min${m === 1 ? '' : 's'} ago`
  }
  if (diff < day) {
    const h = Math.floor(diff / hour)
    return `${h} hr${h === 1 ? '' : 's'} ago`
  }
  if (diff < 30 * day) {
    const d = Math.floor(diff / day)
    return `${d} day${d === 1 ? '' : 's'} ago`
  }
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

/**
 * The tray's short time: "now", "5 min", "2 h", "Yesterday", "3 d", then the date ("Oct 3").
 * A row has one line for it, so it is shorter than `timeAgo`, which the conversation keeps.
 * Hours run through today (and the last 6 h across midnight); "Yesterday" and "N d" count
 * calendar days in the reader's zone.
 */
export function shortTimeAgo(iso: string, now: Date = new Date()): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) {
    // A missing or garbled timestamp: no time beats "Invalid Date" in the row.
    return ''
  }
  const diff = now.getTime() - date.getTime()
  if (diff < minute) {
    return 'now'
  }
  if (diff < hour) {
    return `${Math.floor(diff / minute)} min`
  }
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
  if (date.getTime() >= startOfToday || diff < 6 * hour) {
    return `${Math.floor(diff / hour)} h`
  }
  const startOfThatDay = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()
  const days = Math.round((startOfToday - startOfThatDay) / day)
  if (days <= 1) {
    return 'Yesterday'
  }
  if (days < 30) {
    return `${days} d`
  }
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

/**
 * A comment's first non-empty line as plain text: the widget stores plain text, one paragraph per
 * line, but a pasted body may still carry tags or markdown-ish markers — dropped for a one-liner.
 */
export function commentSnippet(body: string): string {
  const line =
    body
      .replace(/<[^>]*>/g, ' ')
      .split(/\r?\n/)
      .map(l => l.trim())
      .find(Boolean) ?? ''
  return line
    .replace(/^(?:>|#{1,6}|[-*+]|\d+\.)\s+/, '')
    .replace(/[*_`~]+/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

/** "1 comment" / "406 comments". */
export function commentsLabel(n: number): string {
  return `${n} comment${n === 1 ? '' : 's'}`
}
